import { useEffect, useState, useCallback, useRef } from 'react';
import { Button, Table, Badge } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
import Spinner from '../../../shared/spinner';
import CustomAlert from '../../../shared/customAlert';
import { useVerificaPermissao } from '../../../shared/useVerificaPermissao';
import IntegracaoService from '../../../services/integracaoService';
import { integracaoEnum } from '../../../shared/constantes/integracaoEnum';
import { motivosSerasaEnum } from '../../../shared/constantes/motivosSerasaEnum';
import { motivosPh3aReceitaEnum } from '../../../shared/constantes/motivosPh3aReceitaEnum';
import { motivosPh3aProcessosEnum } from '../../../shared/constantes/motivosPh3aProcessosEnum';
import IntegracaoOrigemModal from './integracaoOrigemComponent';
import { motivosBmsPagamentosEnum } from '../../../shared/constantes/motivosBmsPagamentosEnum';
import { IntegracaoDTO, ParametroValorDTO, OrigemIntegracaoDTO, CriarIntegracaoRequestDTO } from './integracaoInterfaces';
import DadosGeraisCard from './components/dadosGeraisCard';
import OrigensTableCard from './components/origensTableCard';
import DadosEspecificosCard from './components/dadosEspecificosCard';
import MotivosGeraisCard from './components/motivosGeraisCard';
import Ph3aJudicialCard from './components/ph3aJudicialCard';
import CertificadoOperadorCard, { CertificadoOperadorCardRef } from './components/certificadoOperadorCard';
import NotificacaoSmtpCard, { NotificacaoSmtpCardRef } from './components/notificacaoSmtpCard';

function IntegracaoFormComponent() {
  useVerificaPermissao();
  const location = useLocation();
  const { id } = location.state || {};
  
  const service = new IntegracaoService();
  const certificadoCardRef = useRef<CertificadoOperadorCardRef>(null);
  const notificacaoSmtpCardRef = useRef<NotificacaoSmtpCardRef>(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);

  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('');
  const [integracaoSelecionada, setIntegracaoSelecionada] = useState('');
  const [tentativa, setTentativa] = useState(1);
  
  const [parametrosDefinicao, setParametrosDefinicao] = useState<any[]>([]);
  const [listaMotivosInternos, setListaMotivosInternos] = useState<any[]>([]);
  const [valoresEspecificos, setValoresEspecificos] = useState<{[key: string]: string}>({});

  const [mapeamentoMotivosGerais, setMapeamentoMotivosGerais] = useState<{[key: string]: string}>({});
  
  const [ph3aPendenciaSelecionados, setPh3aPendenciaSelecionados] = useState<any[]>([]);
  const [ph3aReprovaSelecionados, setPh3aReprovaSelecionados] = useState<any[]>([]);
  const [motivoInternoPendenciaId, setMotivoInternoPendenciaId] = useState('');
  const [motivoInternoReprovaId, setMotivoInternoReprovaId] = useState('');

  const [listaOrigens, setListaOrigens] = useState<any[]>([]);
  
  const [modalOpen, setModalOpen] = useState(false);
  const [origemEmEdicao, setOrigemEmEdicao] = useState<any>(null);

  useEffect(() => {
    carregarDependencias();
  }, []);

  const isBackoffice = integracaoEnum.find(i => i.integracao === integracaoSelecionada)?.tipo === 'BACKOFFICE';
  const isMotor = tipo === 'MOTOR';
  const isNotificacaoSmtp = tipo === 'NOTIFICAÇÕES' && integracaoSelecionada === 'SMTP';
  const isDocSign = tipo === 'ASSINATURA DIGITAL' && integracaoSelecionada === 'DOCSIGN';
  const parametrosObrigatoriosDocSign = ['INTEGRATION_KEY', 'USER_ID', 'ACCOUNT_ID', 'ACCOUNT_BASE_URI', 'OAUTH_BASE_URL', 'PRIVATE_KEY'];
  const labelsParametrosDocSign: {[key: string]: string} = {
    INTEGRATION_KEY: 'Integration Key (Client ID)',
    USER_ID: 'ID de usuário',
    ACCOUNT_ID: 'Account ID',
    ACCOUNT_BASE_URI: 'URI base da conta',
    OAUTH_BASE_URL: 'Ambiente DocSign',
    PRIVATE_KEY: 'Chave privada RSA',
    POLLING_FIXED_DELAY_MS: 'Intervalo de consulta de status (ms)'
  };
  const camposGuidDocSign = ['INTEGRATION_KEY', 'USER_ID', 'ACCOUNT_ID'];
  const guidDocSignRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  const parametroOAuthDocSign = 'OAUTH_BASE_URL';
  const urlOAuthHomologacaoDocSign = 'https://account-d.docusign.com';
  const parametroPollingDocSign = 'POLLING_FIXED_DELAY_MS';
  const valorPadraoPollingDocSignMs = '900000';

  const normalizarParametrosDefinicao = (definicoes: any[], integracao: string) => {
    if (integracao === 'DOCSIGN') {
        const parametrosLegados = ['URL_ENVIO_CONTRATO', 'TOKEN_API', 'CALLBACK_URL'];
        const ordemDocSign: {[key: string]: number} = {
            INTEGRATION_KEY: 1,
            USER_ID: 2,
            ACCOUNT_ID: 3,
            ACCOUNT_BASE_URI: 4,
            OAUTH_BASE_URL: 5,
            PRIVATE_KEY: 6,
            POLLING_FIXED_DELAY_MS: 7
        };
        return definicoes
            .filter((def) => !parametrosLegados.includes(def.nomeParametro))
            .sort((a, b) => (ordemDocSign[a.nomeParametro] || 99) - (ordemDocSign[b.nomeParametro] || 99));
    }
    return definicoes;
  };

  const handleTipoChange = (novoTipo: string) => {
    setTipo(novoTipo);

    const opcoesDoTipo = integracaoEnum.filter(i => i.tipo === novoTipo);
    if (opcoesDoTipo.length === 1) {
        handleIntegracaoChange(opcoesDoTipo[0].integracao);
    } else {
        setIntegracaoSelecionada('');
        setParametrosDefinicao([]);
        setMapeamentoMotivosGerais({});
        setValoresEspecificos({});
        setPh3aPendenciaSelecionados([]);
        setPh3aReprovaSelecionados([]);
    }
  }

  const carregarDependencias = async () => {
    try {
        setLoading(true);
        const respMotivos = await service.listarTodosMotivos();
        setListaMotivosInternos(respMotivos.data);

        if (id) {
            await carregarDadosEdicao(id, respMotivos.data);
        }
    } catch (e: any) {
        setAlert({ message: 'Erro ao carregar dados iniciais.', type: 'error' });
    } finally {
        setLoading(false);
    }
  }

  const carregarDadosEdicao = async (idIntegracao: number, motivosInternos: any[]) => {
    // console.log("=== DEBUG CARREGAMENTO ===");
    console.log("ID da Integração sendo carregada:", idIntegracao);

    const response = await service.listarPorId(idIntegracao);
    const dados = response.data;

    setNome(dados.nome);
    setTipo(dados.tipo);

    console.log("Dados brutos recebidos do Backend:", dados);
    console.log("Lista de parâmetros recebidos:", dados.parametros);

    setIntegracaoSelecionada(dados.integracao);

    const respDefinicoes = await service.listarParametrosDefinicao(dados.integracao);
    const definicoes = normalizarParametrosDefinicao(respDefinicoes.data || [], dados.integracao);
    setParametrosDefinicao(definicoes);

    const credenciaisSalvas: {[key: string]: string} = {};
    const motivosGeraisSalvos: {[key: string]: string} = {};
    const dadosEspecificosSalvos: {[key: string]: string} = {};
    
    const tempPendencia: any[] = [];
    const tempReprova: any[] = [];
    let tempMotivoPendenciaId = '';
    let tempMotivoReprovaId = '';

    if(dados.parametros){

const paramTentativa = dados.parametros.find((p: any) => p.id === 130 || p.nomeParametro === 'TENTATIVAS');
        
        console.log("Parâmetro de Tentativa encontrado:", paramTentativa);

        dados.parametros.forEach((p: any) => {
            const def = definicoes.find((d: any) => d.id === p.id || d.nomeParametro === p.nomeParametro);
            if (!def) return;

            if (def.tipoParametro === 'CAMPO') {
                dadosEspecificosSalvos[def.nomeParametro] = p.valor;
            } 
            else if (def.tipoParametro === 'MOTIVO') {
                const isJudicial = motivosPh3aProcessosEnum.find(m => m.descricaoBanco === def.nomeParametro);
                
                if (isJudicial) {
                    const motivoInterno = motivosInternos.find(m => String(m.id) === String(p.valor));
                    
                    if (motivoInterno) {
                        if (motivoInterno.pendenciaRecusa === 'R') {
                            tempReprova.push(isJudicial);
                            tempMotivoReprovaId = p.valor;
                        } else {
                            tempPendencia.push(isJudicial);
                            tempMotivoPendenciaId = p.valor;
                        }
                    }
                } else {
                    motivosGeraisSalvos[def.nomeParametro] = p.valor;
                }
            }
            const paramTentativa = dados.parametros.find((p: any) => p.id === 130 || p.nomeParametro === 'TENTATIVAS');
    if (paramTentativa) {
        setTentativa(Number(paramTentativa.valor));
    }
        });
    }

    if (dados.integracao === 'DOCSIGN'
        && definicoes.some((def: any) => def.nomeParametro === parametroPollingDocSign)
        && !dadosEspecificosSalvos[parametroPollingDocSign]) {
        dadosEspecificosSalvos[parametroPollingDocSign] = valorPadraoPollingDocSignMs;
    }
    if (dados.integracao === 'DOCSIGN'
        && definicoes.some((def: any) => def.nomeParametro === parametroOAuthDocSign)
        && !dadosEspecificosSalvos[parametroOAuthDocSign]) {
        dadosEspecificosSalvos[parametroOAuthDocSign] = urlOAuthHomologacaoDocSign;
    }

    setMapeamentoMotivosGerais(motivosGeraisSalvos);
    setValoresEspecificos(dadosEspecificosSalvos);
    
    setPh3aPendenciaSelecionados(tempPendencia);
    setPh3aReprovaSelecionados(tempReprova);
    setMotivoInternoPendenciaId(tempMotivoPendenciaId);
    setMotivoInternoReprovaId(tempMotivoReprovaId);
    
    if (dados.origens && dados.origens.length > 0) {
        
        const promisesOrigens = dados.origens.map(async (o: OrigemIntegracaoDTO) => {
            
            const credsMap: {[key: string]: string} = {};
            if (o.parametros) {
                o.parametros.forEach(param => {
                    const def = definicoes.find((d: any) => d.id === param.parametroDefinicaoId);
                    if (def) {
                        credsMap[def.nomeParametro] = param.valor;
                    }
                });
            }

            let tipoDesc = 'TODAS';
            if (o.tipoOrigem === 1) tipoDesc = 'MATRIZ';
            else if (o.tipoOrigem === 6) tipoDesc = 'ORIGINADOR';
            
            let nomeOrigemVisual = o.nomeOrigem || `Origem ID ${o.idOrigem}`;

            if (o.tipoOrigem === 0) {
                nomeOrigemVisual = 'Todas as Origens';
            } 
            else if (o.idOrigem) {
                try {
                    let responseNome;
                    
                    if (o.tipoOrigem === 1) {
                        responseNome = await service.buscarMatrizPorId(o.idOrigem);
                    } else if (o.tipoOrigem === 6) {
                        responseNome = await service.buscarOriginadorPorId(o.idOrigem);
                    }

                    if (responseNome && responseNome.data) {
                        const registro = Array.isArray(responseNome.data) ? responseNome.data[0] : responseNome.data;
                        
                        if (registro && registro.nome) {
                            nomeOrigemVisual = registro.nome;
                        }
                    }
                } catch (error) {
                    console.warn(`Não foi possível carregar o nome para a origem ID ${o.idOrigem}`, error);
                }
            }

            return {
                uuid: crypto.randomUUID(), 
                id: o.id,
                tipoOrigemDesc: tipoDesc,
                tipoOrigemCod: o.tipoOrigem,
                idOrigem: o.idOrigem,
                nomeOrigem: nomeOrigemVisual,
                credenciais: credsMap
            };
        });

        const origensFormatadas = await Promise.all(promisesOrigens);
        setListaOrigens(origensFormatadas);
        
    } else {
        setListaOrigens([]);
    }
  }

  const handleIntegracaoChange = async (novaIntegracao: string) => {
    if (!novaIntegracao) {
        setIntegracaoSelecionada('');
        setParametrosDefinicao([]);
        setMapeamentoMotivosGerais({});
        setValoresEspecificos({});
        setPh3aPendenciaSelecionados([]);
        setPh3aReprovaSelecionados([]);
        setMotivoInternoPendenciaId('');
        setMotivoInternoReprovaId('');
        return;
    }

    setLoading(true);
    try {
        if (novaIntegracao === 'SMTP') {
            setIntegracaoSelecionada(novaIntegracao);
            setNome((nomeAtual) => nomeAtual || 'Notificações SMTP');
            setParametrosDefinicao([]);
            setMapeamentoMotivosGerais({});
            setValoresEspecificos({});
            setPh3aPendenciaSelecionados([]);
            setPh3aReprovaSelecionados([]);
            setMotivoInternoPendenciaId('');
            setMotivoInternoReprovaId('');
            setListaOrigens([]);
            return;
        }

        const checkResp = await service.verificarGeneralizada(novaIntegracao);
        const idGeneralizadaExistente = checkResp.data;

        if (idGeneralizadaExistente && (!id || Number(id) !== Number(idGeneralizadaExistente))) {
            
            setAlert({ 
                message: `Não é possível criar outra integração ${novaIntegracao}, pois já existe no sistema uma integração ${novaIntegracao} generalizada para todas as origens.`, 
                type: 'warning' 
            });
            
            setIntegracaoSelecionada(''); 
            setLoading(false);
            return;
        }

        setIntegracaoSelecionada(novaIntegracao);
        
        setParametrosDefinicao([]);
        setMapeamentoMotivosGerais({});
        setValoresEspecificos({});
        setPh3aPendenciaSelecionados([]);
        setPh3aReprovaSelecionados([]);
        setMotivoInternoPendenciaId('');
        setMotivoInternoReprovaId('');

        const response = await service.listarParametrosDefinicao(novaIntegracao);
        const definicoes = normalizarParametrosDefinicao(response.data || [], novaIntegracao);
        setParametrosDefinicao(definicoes);
        if (novaIntegracao === 'DOCSIGN') {
            const valoresIniciaisDocSign: {[key: string]: string} = {};
            if (definicoes.some((def) => def.nomeParametro === parametroOAuthDocSign)) {
                valoresIniciaisDocSign[parametroOAuthDocSign] = urlOAuthHomologacaoDocSign;
            }
            if (definicoes.some((def) => def.nomeParametro === parametroPollingDocSign)) {
                valoresIniciaisDocSign[parametroPollingDocSign] = valorPadraoPollingDocSignMs;
            }
            setValoresEspecificos(valoresIniciaisDocSign);
        }

    } catch (error: any) {
        console.error(error);
        setAlert({ message: 'Erro ao validar ou buscar parâmetros.', type: 'error' });
        setIntegracaoSelecionada('');
    } finally {
        setLoading(false);
    }
  }

  const motivosInternosPendencia = listaMotivosInternos.filter(m => ['P', 'S'].includes(m.pendenciaRecusa));
  const motivosInternosReprova = listaMotivosInternos.filter(m => m.pendenciaRecusa === 'R');

  const getDisponiveisPendencia = () => {
    return motivosPh3aProcessosEnum.filter(m => 
        parametrosDefinicao.some(def => def.nomeParametro === m.descricaoBanco) &&
        !ph3aPendenciaSelecionados.includes(m) && 
        !ph3aReprovaSelecionados.includes(m)
    );
  }

  const getDisponiveisReprova = () => {
    return motivosPh3aProcessosEnum.filter(m => 
        parametrosDefinicao.some(def => def.nomeParametro === m.descricaoBanco) &&
        !ph3aPendenciaSelecionados.includes(m) && 
        !ph3aReprovaSelecionados.includes(m)
    );
  }

  const handleNovaOrigem = () => {
    setOrigemEmEdicao(null);
    setModalOpen(true);
  }

  const handleEditarOrigem = (origem: any) => {
    setOrigemEmEdicao(origem);
    setModalOpen(true);
  }

  const handleSalvarModalOrigem = async (config: any) => {
    
    if (config.tipoOrigemDesc === 'TODAS') {
        
        if (!integracaoSelecionada) {
             setAlert({ message: 'Selecione uma integração antes de adicionar a origem.', type: 'warning' });
             return;
        }

        try {
            setLoading(true);
            
            const response = await service.verificarSeExisteOutra(integracaoSelecionada, id ? Number(id) : undefined);
            const existeOutra = response.data;

            if (existeOutra) {
                setAlert({ 
                    message: `Já existe uma integração ${integracaoSelecionada} cadastrada no sistema. Tornando impossivel o cadastro de uma integração ${integracaoSelecionada} com tipo de origem 'Todas' (generalizada).`, 
                    type: 'error' 
                });
                setLoading(false);
                return;
            }

        } catch (error) {
            console.error(error);
            setAlert({ message: 'Erro ao validar origem generalizada.', type: 'error' });
            setLoading(false);
            return;
        } finally {
            setLoading(false);
        }
    }

    setListaOrigens(prev => {
        const index = prev.findIndex(o => o.uuid === config.uuid);
        if (index >= 0) {
            const nova = [...prev];
            nova[index] = { ...config, id: nova[index].id };
            return nova;
        }
        return [...prev, config];
    });

    setModalOpen(false); 
  }

  const handleExcluirOrigem = (uuid: string) => {
    if (window.confirm('Remover esta origem da lista?')) {
        setListaOrigens(prev => prev.filter(o => o.uuid !== uuid));
    }
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (isNotificacaoSmtp) {
        setLoading(true);
        try {
            await notificacaoSmtpCardRef.current?.salvarConfiguracao();
            const idIntegracaoSmtp = id
                ? Number(id)
                : Number((await service.verificarGeneralizada('SMTP')).data || 0) || null;

            await service.salvar({
                integracao: {
                    id: idIntegracaoSmtp,
                    nome: nome?.trim() || 'Notificações SMTP',
                    tipo: 'NOTIFICAÇÕES',
                    integracao: 'SMTP',
                },
                parametros: [],
                generalizada: true,
                origens: [{
                    idOrigem: 0,
                    tipoOrigem: 0,
                    parametros: [],
                }],
            });
            setAlert({ message: 'Configuração SMTP salva com sucesso!', type: 'success' });
            setTimeout(() => window.history.back(), 2000);
        } catch (err: any) {
            setAlert({ message: 'Erro ao salvar SMTP: ' + (err.response?.data || err.message), type: 'error' });
        } finally {
            setLoading(false);
        }
        return;
    }

    if (isDocSign) {
        const camposFaltantes = parametrosObrigatoriosDocSign.filter((nomeParametro) => {
            const existeDefinicao = parametrosDefinicao.some((def) => def.nomeParametro === nomeParametro);
            const valor = String(valoresEspecificos[nomeParametro] || '').trim();
            return !existeDefinicao || !valor;
        });

        if (camposFaltantes.length > 0) {
            setAlert({
                message: `Configuração DocSign incompleta. Preencha: ${camposFaltantes.map((campo) => labelsParametrosDocSign[campo] || campo).join(', ')}.`,
                type: 'warning'
            });
            return;
        }

        const camposGuidInvalidos = camposGuidDocSign.filter((nomeParametro) => {
            const valor = String(valoresEspecificos[nomeParametro] || '').trim();
            return valor && !guidDocSignRegex.test(valor);
        });

        if (camposGuidInvalidos.length > 0) {
            setAlert({
                message: `Configuração DocSign inválida. Confira ${camposGuidInvalidos.map((campo) => labelsParametrosDocSign[campo] || campo).join(', ')}. Copie o GUID direto da DocuSign e verifique se não trocou zero (0) por letra O.`,
                type: 'warning'
            });
            return;
        }

        const pollingFixedDelayMs = String(valoresEspecificos[parametroPollingDocSign] || '').trim();
        if (pollingFixedDelayMs
            && (!/^\d+$/.test(pollingFixedDelayMs)
                || Number(pollingFixedDelayMs) < Number(valorPadraoPollingDocSignMs))) {
            setAlert({
                message: 'Intervalo de consulta de status da DocSign deve ser numérico e maior ou igual a 900000 ms (15 minutos).',
                type: 'warning'
            });
            return;
        }
    }

    if (listaOrigens.length === 0) {
        setAlert({ message: 'Adicione pelo menos uma origem.', type: 'warning' });
        return;
    }

    const isPh3a = integracaoSelecionada.toLowerCase().includes('ph3a');
    if (isPh3a) {
        if (ph3aPendenciaSelecionados.length > 0 && !motivoInternoPendenciaId) {
            setAlert({ message: 'Selecione o Motivo Interno para os itens de Pendência.', type: 'warning' });
            return;
        }
        if (ph3aReprovaSelecionados.length > 0 && !motivoInternoReprovaId) {
            setAlert({ message: 'Selecione o Motivo Interno para os itens de Reprova.', type: 'warning' });
            return;
        }
    }

    setLoading(true);
    try {
        const listaParametrosGerais: ParametroValorDTO[] = [];

        Object.keys(mapeamentoMotivosGerais).forEach(key => {
            const def = parametrosDefinicao.find(d => d.nomeParametro === key);
            if (def) listaParametrosGerais.push({ parametroDefinicaoId: def.id, valor: mapeamentoMotivosGerais[key] });
        });

        Object.keys(valoresEspecificos).forEach(key => {
            const def = parametrosDefinicao.find(d => d.nomeParametro === key);
            if (def) listaParametrosGerais.push({ parametroDefinicaoId: def.id, valor: valoresEspecificos[key] });
        });

        if (isPh3a) {
            ph3aPendenciaSelecionados.forEach(judicial => {
                const def = parametrosDefinicao.find(d => d.nomeParametro === judicial.descricaoBanco);
                if (def) listaParametrosGerais.push({ parametroDefinicaoId: def.id, valor: motivoInternoPendenciaId });
            });
            
            ph3aReprovaSelecionados.forEach(judicial => {
                const def = parametrosDefinicao.find(d => d.nomeParametro === judicial.descricaoBanco);
                if (def) listaParametrosGerais.push({ parametroDefinicaoId: def.id, valor: motivoInternoReprovaId });
            });
        }

        const listaOrigensDTO: OrigemIntegracaoDTO[] = listaOrigens.map(origem => {
            
            const paramsCredenciais: ParametroValorDTO[] = [];
            
            Object.keys(origem.credenciais).forEach(key => {
                const def = parametrosDefinicao.find(d => d.nomeParametro === key);
                if (def) {
                    paramsCredenciais.push({ 
                        parametroDefinicaoId: def.id, 
                        valor: origem.credenciais[key] 
                    });
                }
            });

            return {
                id: origem.id || null,
                idOrigem: Number(origem.idOrigem),
                tipoOrigem: origem.tipoOrigemCod,
                parametros: paramsCredenciais
            };
        });

        const isGeneralizada = listaOrigens.some(o => o.tipoOrigemDesc === 'TODAS');

        const dtoIntegracao: IntegracaoDTO = {
            id: id ? Number(id) : null,
            nome: nome,
            tipo: tipo,
            integracao: integracaoSelecionada,
            tentativa: tentativa
        };

        const payload: CriarIntegracaoRequestDTO = {
            integracao: dtoIntegracao,
            parametros: listaParametrosGerais,
            origens: listaOrigensDTO,
            generalizada: isGeneralizada
        };

        

        await service.salvar(payload);

        if (integracaoSelecionada === 'CRCP' && certificadoCardRef.current) {
          await certificadoCardRef.current.salvarSelecaoCertificados();
        }

        setAlert({ message: 'Dados salvos com sucesso!', type: 'success' });
        setTimeout(() => window.history.back(), 2000);

    } catch (err: any) {
        console.error(err);
        setAlert({ message: 'Erro ao salvar: ' + (err.response?.data || err.message), type: 'error' });
    } finally {
        setLoading(false);
    }
  }

  const renderStatusBadge = (idMotivo: string) => {
    if (!idMotivo) return null;
    const motivo = listaMotivosInternos.find(m => String(m.id) === String(idMotivo));
    
    if (motivo) {
        if (motivo.pendenciaRecusa === 'R') {
            return <Badge bg="danger" className="ms-2">Reprova</Badge>;
        } else {
            return <Badge bg="warning" text="dark" className="ms-2">Pendência</Badge>;
        }
    }
    return null;
  }

  const renderMotivosGerais = () => {
    let listaEnum: any[] = [];
    if (integracaoSelecionada.toLowerCase().includes('serasa')) listaEnum = motivosSerasaEnum;
    else if (integracaoSelecionada.toLowerCase().includes('ph3a')) listaEnum = motivosPh3aReceitaEnum;
    else if (integracaoSelecionada.toLowerCase().includes('bms')) listaEnum = motivosBmsPagamentosEnum;

    const defsGerais = parametrosDefinicao.filter(p => 
        p.tipoParametro === 'MOTIVO' && 
        listaEnum.some(e => e.descricaoBanco === p.nomeParametro)
    );

    if (defsGerais.length === 0) return <small className="text-muted">Nenhum motivo geral parametrizado.</small>;

    return defsGerais.map(p => {
        const label = listaEnum.find(m => m.descricaoBanco === p.nomeParametro)?.motivo || p.nomeParametro;
        const labelTexto = label?.motivo || p.nomeParametro;
        const valorSelecionado = mapeamentoMotivosGerais[p.nomeParametro] || '';
        return (
            <div className="row mb-2 align-items-center" key={p.id}>
                <div className="col-5">
                    <input className="form-control form-control-sm" value={label} disabled />
                </div>
                
                <div className="col-1 text-center">➡</div>
                
                <div className="col-6 d-flex align-items-center">
                    <div style={{ flexGrow: 1 }}>
                        <select 
                            className="form-select form-select-sm"
                            value={valorSelecionado}
                            onChange={e => setMapeamentoMotivosGerais({...mapeamentoMotivosGerais, [p.nomeParametro]: e.target.value})}
                        >
                            <option value="">Selecione Ação...</option>
                            {listaMotivosInternos.map(m => <option key={m.id} value={m.id}>{m.descricao}</option>)}
                        </select>
                    </div>
                    <div style={{ width: '80px', textAlign: 'left' }}>
                        {renderStatusBadge(valorSelecionado)}
                    </div>
                </div>
            </div>
        );
    });
  };

  const renderDadosEspecificos = () => {
    const campos = parametrosDefinicao.filter(p => p.tipoParametro === 'CAMPO');

    if (campos.length === 0) return <div className="text-muted p-3">Nenhum dado específico necessário. Configure as credenciais na origem.</div>;

    return (
        <div className="row">
            {campos.map(p => (
                <div className="row">
            {campos.map(p => (
                <div className="col-md-6 mb-3" key={p.id}>
                    <label className="form-label">
                        {p.nomeParametro.replace(/_/g, ' ')}:
                    </label>
                    <input 
                        type="text" 
                        className="form-control"
                        placeholder={`Informe ${p.nomeParametro.replace(/_/g, ' ')}`}
                        value={valoresEspecificos[p.nomeParametro] || ''}
                        onChange={e => setValoresEspecificos({...valoresEspecificos, [p.nomeParametro]: e.target.value})}
                    />
                </div>
            ))}
        </div>
            ))}
        </div>
    );
  }

    const handleTentativaChange = (v: any) => {
    setTentativa(Number(v));
};

  return (
    <div className="container pb-5">
      {alert && <CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
      {loading && <Spinner loading={loading} />}

      <form onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between mt-3 mb-3">
            <h5>{id ? 'Edição de Integração' : 'Nova Configuração'}</h5>
            <Button variant="secondary" onClick={() => window.history.back()}>Voltar</Button>
        </div>

        <DadosGeraisCard 
            nome={nome} setNome={setNome}
            tipo={tipo}
            handleTipoChange={handleTipoChange}
            integracaoSelecionada={integracaoSelecionada}
            handleIntegracaoChange={handleIntegracaoChange}
            tentativa={tentativa}
            handleTentativaChange={handleTentativaChange}
        />

        {!isNotificacaoSmtp && (
            <OrigensTableCard 
                listaOrigens={listaOrigens}
                handleNovaOrigem={handleNovaOrigem}
                handleEditarOrigem={handleEditarOrigem}
                handleExcluirOrigem={handleExcluirOrigem}
                integracaoSelecionada={integracaoSelecionada}
            />
        )}

        {isNotificacaoSmtp && <NotificacaoSmtpCard ref={notificacaoSmtpCardRef} />}

        {integracaoSelecionada && !isNotificacaoSmtp && (
            <>
                {(((isBackoffice || isMotor) && !integracaoSelecionada.toLowerCase().includes('bms')) || isDocSign) ? (
                    <DadosEspecificosCard 
                        parametrosDefinicao={parametrosDefinicao}
                        valoresEspecificos={valoresEspecificos}
                        setValoresEspecificos={setValoresEspecificos}
                        camposObrigatorios={isDocSign ? parametrosObrigatoriosDocSign : []}
                    />
                ) : (
                    <MotivosGeraisCard 
                        integracaoSelecionada={integracaoSelecionada}
                        parametrosDefinicao={parametrosDefinicao}
                        listaMotivosInternos={listaMotivosInternos}
                        mapeamentoMotivosGerais={mapeamentoMotivosGerais}
                        setMapeamentoMotivosGerais={setMapeamentoMotivosGerais}
                    />
                )}

                {!isBackoffice && integracaoSelecionada.toLowerCase().includes('ph3a') && (
                     <Ph3aJudicialCard 
                        parametrosDefinicao={parametrosDefinicao}
                        motivosInternosPendencia={motivosInternosPendencia}
                        motivosInternosReprova={motivosInternosReprova}
                        ph3aPendenciaSelecionados={ph3aPendenciaSelecionados}
                        setPh3aPendenciaSelecionados={setPh3aPendenciaSelecionados}
                        ph3aReprovaSelecionados={ph3aReprovaSelecionados}
                        setPh3aReprovaSelecionados={setPh3aReprovaSelecionados}
                        motivoInternoPendenciaId={motivoInternoPendenciaId}
                        setMotivoInternoPendenciaId={setMotivoInternoPendenciaId}
                        motivoInternoReprovaId={motivoInternoReprovaId}
                        setMotivoInternoReprovaId={setMotivoInternoReprovaId}
                     />
                )}
            </>
        )}

        {integracaoSelecionada === 'CRCP' && <CertificadoOperadorCard ref={certificadoCardRef} />}

        <div className="d-flex justify-content-end mb-5">
            <Button variant="success" size="lg" type="submit" disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
        </div>
      </form>

      <IntegracaoOrigemModal 
        show={modalOpen} 
        onHide={() => setModalOpen(false)}
        onSave={handleSalvarModalOrigem}
        definicoes={parametrosDefinicao}
        origemEdicao={origemEmEdicao}
        listaOrigensExistentes={listaOrigens}
      />
    </div>
  );
}

export default IntegracaoFormComponent;
